import { ChipProps } from '../../atoms/Chip/Chip.types';
import { CheckboxItemProps } from '../../atoms/CheckboxItem/CheckboxItem.types';
import { OptionLike, OptionLikeKey } from './ChipInput.types';

/**
 * Normalizes string array or OptionLike array to OptionLike array
 */
export const normalizeToOptionLike = (options: string[] | OptionLike[]): OptionLike[] => {
  if (!options || options.length === 0) return [];
  
  // Check if first item is OptionLike (has key and label properties)
  const firstOption = options[0];
  if (typeof firstOption === 'object' && 'key' in firstOption && 'label' in firstOption) {
    return options as OptionLike[];
  }
  
  // Convert strings to OptionLike
  return (options as string[]).map(option => ({
    key: option,
    label: option,
  }));
};

/**
 * Normalizes string array or OptionLike array to OptionLikeKey array
 */
export const normalizeToOptionLikeKey = (options: string[] | OptionLike[]): OptionLikeKey[] => {
  return normalizeToOptionLike(options).map(option => option.key);
};

/**
 * Creates chip props for a selected value using OptionLike
 */
export const createChipProps = (
  option: OptionLike,
  onRemove: (key: OptionLikeKey) => void,
  className?: string
): ChipProps => ({
  children: option.label,
  isDismissible: true,
  onClose: () => onRemove(option.key),
  style: 'F',
  size: 'Small',
  type: 'Subdued',
  corner: 'Squared',
  className,
});

/**
 * Creates checkbox item props for context menu options using OptionLike
 */
export const createCheckboxItemProps = (
  option: OptionLike,
  isChecked: boolean,
  onSelect: (key: OptionLikeKey) => void,
  className?: string
): CheckboxItemProps => ({
  id: String(option.key),
  children: option.label,
  className,
  onValueChange: () => onSelect(option.key),
  checked: isChecked,
  labelAlignment: 'Right',
});

/**
 * Toggles an option key in the selected keys array
 */
export const toggleOptionInArray = (
  selectedKeys: OptionLikeKey[],
  optionKey: OptionLikeKey
): OptionLikeKey[] => {
  if (!selectedKeys.includes(optionKey)) {
    return [...selectedKeys, optionKey];
  }
  return selectedKeys.filter(key => key !== optionKey);
};

/**
 * Adds a new OptionLike to available options if it doesn't exist
 */
export const addUniqueOption = (
  availableOptions: OptionLike[],
  newOption: OptionLike
): OptionLike[] => {
  const exists = availableOptions.some(option => option.key === newOption.key);
  if (!exists) {
    return [...availableOptions, newOption];
  }
  return availableOptions;
};

/**
 * Handles Enter key press for adding new chips
 */
export const handleEnterKeyForChip = (
  event: React.KeyboardEvent<HTMLInputElement>,
  inputValue: string,
  onAddChip: (value: string) => void,
  onClearInput: () => void
): void => {
  if (event.key === "Enter") {
    event.preventDefault();
    const trimmedValue = inputValue.trim();
    if (trimmedValue) {
      onAddChip(trimmedValue);
      onClearInput();
    }
  }
};

/**
 * Finds an OptionLike by its key
 */
export const findOptionByKey = (
  options: OptionLike[],
  key: OptionLikeKey
): OptionLike | undefined => {
  return options.find(option => option.key === key);
};

/**
 * Converts OptionLikeKey array back to original format (string[] or OptionLike[])
 */
export const convertKeysToOriginalFormat = <T extends string[] | OptionLike[]>(
  keys: OptionLikeKey[],
  originalValue: T | undefined,
  availableOptions: OptionLike[]
): T => {
  // If no original value, default to string[] format
  if (!originalValue || originalValue.length === 0) {
    return keys as T;
  }
  
  // Check if original was OptionLike[]
  const firstItem = originalValue[0];
  if (typeof firstItem === 'object' && 'key' in firstItem && 'label' in firstItem) {
    // Convert back to OptionLike[]
    return keys.map(key => findOptionByKey(availableOptions, key)).filter(Boolean) as T;
  } else {
    // Convert back to string[]
    return keys as T;
  }
};

/**
 * Filters options by input value for search functionality
 */
export const filterOptionsByInput = (options: OptionLike[], inputValue: string): OptionLike[] => {
  if (!inputValue.trim()) return options;
  const searchTerm = inputValue.toLowerCase();
  return options.filter(option => 
    option.label.toLowerCase().includes(searchTerm)
  );
};

/**
 * Creates an optimistic item for async operations
 */
export const createOptimisticItem = (value: string): OptionLike => ({
  key: `temp-${Date.now()}`,
  label: value
});

/**
 * Adds a key to selected keys array
 */
export const addKeyToSelection = (
  selectedKeys: OptionLikeKey[],
  keyToAdd: OptionLikeKey
): OptionLikeKey[] => {
  if (selectedKeys.includes(keyToAdd)) return selectedKeys;
  return [...selectedKeys, keyToAdd];
};

/**
 * Removes a key from selected keys array
 */
export const removeKeyFromSelection = (
  selectedKeys: OptionLikeKey[],
  keyToRemove: OptionLikeKey
): OptionLikeKey[] => {
  return selectedKeys.filter(key => key !== keyToRemove);
};

/**
 * Replaces a temporary key with a real key in selected keys array
 */
export const replaceKeyInSelection = (
  selectedKeys: OptionLikeKey[],
  oldKey: OptionLikeKey,
  newKey: OptionLikeKey
): OptionLikeKey[] => {
  return selectedKeys.map(key => key === oldKey ? newKey : key);
};

/**
 * Handles async add item operation with optimistic updates
 */
export const handleAsyncAddItem = async (
  inputValue: string,
  onAddItem: (value: string) => Promise<OptionLike>,
  currentOptions: OptionLike[],
  currentSelectedKeys: OptionLikeKey[],
  setOptions: (options: OptionLike[]) => void,
  setSelectedKeys: (keys: OptionLikeKey[]) => void
): Promise<void> => {
  const tempOption = createOptimisticItem(inputValue);
  
  // Optimistic updates
  setOptions(addUniqueOption(currentOptions, tempOption));
  setSelectedKeys(addKeyToSelection(currentSelectedKeys, tempOption.key));
  
  try {
    const realOption = await onAddItem(inputValue);
    // Replace temp with real
    setOptions(currentOptions.map(opt => opt.key === tempOption.key ? realOption : opt));
    setSelectedKeys(replaceKeyInSelection(currentSelectedKeys, tempOption.key, realOption.key));
  } catch (error) {
    // Revert optimistic changes
    setOptions(currentOptions.filter(opt => opt.key !== tempOption.key));
    setSelectedKeys(removeKeyFromSelection(currentSelectedKeys, tempOption.key));
    throw error; // Re-throw for caller to handle
  }
};